import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  TextField,
  Autocomplete,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import BaseModal from '../../../../../../../components/modal/baseModal'
import MultiSelectSimple from '../../../../../../../components/dinamicForm/multiSelectSimple'
import FilterSelect from '../../../../../../../components/dinamicForm/filterSelect'
import { StorePerformanceItem, PeriodValidationError } from '../types'
import { isValidDate, getISOWeeksInYear } from '../utils/periodValidation'
import { getISOWeek } from 'date-fns'
import { SxProps, Theme } from '@mui/system'

interface FilterModalProps {
  open: boolean
  onClose: () => void
  periodFrom: Date | null
  setPeriodFrom: (date: Date | null) => void
  periodTo: Date | null
  setPeriodTo: (date: Date | null) => void
  periodWeek: string[]
  setPeriodWeek: (weeks: string[]) => void
  periodYear: number | null
  setPeriodYear: (year: number | null) => void
  periodValidationError: PeriodValidationError | null
  hasPeriodErrors: boolean
  filterDivision: StorePerformanceItem[]
  setFilterDivision: (items: StorePerformanceItem[]) => void
  filterZone: StorePerformanceItem[]
  setFilterZone: (items: StorePerformanceItem[]) => void
  filterUser: StorePerformanceItem[]
  setFilterUser: (items: StorePerformanceItem[]) => void
  filterRole: StorePerformanceItem[]
  setFilterRole: (items: StorePerformanceItem[]) => void
  divisionsOptions: StorePerformanceItem[]
  zonesOptions: StorePerformanceItem[]
  usersOptions: StorePerformanceItem[]
  rolesOptions: StorePerformanceItem[]
  filtersLoading: boolean
  isPartner: boolean
  hideZone?: boolean
  onApply: () => void
  styles: {
    filterDrawerTitle: SxProps<Theme>
    filterSectionTitle: SxProps<Theme>
    filterPeriodRow: SxProps<Theme>
    filterField: SxProps<Theme>
    filterButtonContainer: SxProps<Theme>
    filterApplyButton: SxProps<Theme>
  }
  t: (key: string) => string
}

const getWeekOptions = (year: number) => {
  const currentYear = new Date().getFullYear()
  const currentWeek = getISOWeek(new Date())
  const totalWeeks = getISOWeeksInYear(year)
  const maxWeek = year === currentYear ? currentWeek : totalWeeks
  return Array.from({ length: maxWeek }, (_, i) => ({
    id: String(i + 1),
    label: `Semana ${i + 1}`,
    value: String(i + 1),
  }))
}

const getYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const years: number[] = []
  for (let i = 0; i < 5; i++) {
    const year = currentYear - i
    if (year >= 2026) {
      years.push(year)
    }
  }
  return years.map((year) => ({
    id: String(year),
    label: String(year),
    value: String(year),
  }))
}

const FilterModal = ({
  open,
  onClose,
  periodFrom,
  setPeriodFrom,
  periodTo,
  setPeriodTo,
  periodWeek,
  setPeriodWeek,
  periodYear,
  setPeriodYear,
  periodValidationError,
  hasPeriodErrors,
  filterDivision,
  setFilterDivision,
  filterZone,
  setFilterZone,
  filterUser,
  setFilterUser,
  filterRole,
  setFilterRole,
  divisionsOptions,
  zonesOptions,
  usersOptions,
  rolesOptions,
  filtersLoading,
  isPartner,
  hideZone,
  onApply,
  styles,
  t,
}: FilterModalProps) => {
  const currentYearForWeeks = periodYear ?? new Date().getFullYear()
  const weekOptions = useMemo(() => getWeekOptions(currentYearForWeeks), [currentYearForWeeks])

  const selectedWeekOptions = useMemo(
    () => weekOptions.filter((w) => periodWeek.includes(w.value)),
    [weekOptions, periodWeek]
  )

  const isDateError = periodValidationError && (
    periodValidationError.includes('From') ||
    periodValidationError.includes('To') ||
    periodValidationError.includes('Year') ||
    periodValidationError.includes('365') ||
    periodValidationError.includes('futureDate')
  )

  const isWeekError = periodValidationError && periodValidationError.includes('Weeks')

  const [showAllWeeks, setShowAllWeeks] = useState(false)
  const MAX_VISIBLE_WEEKS = 8
  const hasMoreWeeks = periodWeek.length > MAX_VISIBLE_WEEKS
  const visibleWeeks = showAllWeeks ? periodWeek : periodWeek.slice(0, MAX_VISIBLE_WEEKS)

  return (
    <BaseModal open={open} addClose close={{ onClick: onClose }} sx={{minWidth: '460px', borderRadius: '0px', padding: '6px' }}>
      <Box sx={{ p: '0px 16px 20px', maxWidth: '460px' }}>
        <Typography sx={styles.filterDrawerTitle}>
          {t('dashboard.storePerformance.filter.title')}
        </Typography>

        {/* Period Section */}
        <Box sx={{ mb: '24px' }}>
          <Typography sx={styles.filterSectionTitle}>
            {t('dashboard.storePerformance.filter.period')}
          </Typography>

          {/* Dates Row */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box sx={styles.filterPeriodRow}>
              <DatePicker
                label={t('dashboard.storePerformance.filter.from')}
                minDate={dayjs('2026-01-01')}
                maxDate={periodTo && isValidDate(periodTo) ? dayjs(periodTo) : dayjs()}
                value={periodFrom && isValidDate(periodFrom) ? dayjs(periodFrom) : null}
                onChange={(value) => {
                  const date = value && value.isValid() ? value.toDate() : null
                  setPeriodFrom(date)
                }}
                slotProps={{
                  textField: {
                    sx: styles.filterField,
                  },
                }}
              />
              <DatePicker
                label={t('dashboard.storePerformance.filter.to')}
                minDate={periodFrom && isValidDate(periodFrom) ? dayjs(periodFrom) : dayjs('2026-01-01')}
                maxDate={dayjs()}
                value={periodTo && isValidDate(periodTo) ? dayjs(periodTo) : null}
                onChange={(value) => {
                  const date = value && value.isValid() ? value.toDate() : null
                  setPeriodTo(date)
                }}
                slotProps={{
                  textField: {
                    sx: styles.filterField,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          {isDateError && (
            <Typography sx={{ color: 'error.main', fontSize: '12px', mt: '4px' }}>
              {t(periodValidationError)}
            </Typography>
          )}

          {/* Weeks Row */}
          <Box sx={{ ...styles.filterPeriodRow, mt: '12px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              <Autocomplete
                multiple
                options={weekOptions}
                getOptionLabel={(option: any) => option.label}
                value={selectedWeekOptions}
                filterSelectedOptions
                disableCloseOnSelect
                disabled={periodYear === null}
                noOptionsText={t('general.noOptions')}
                onChange={(_: any, value: any[]) => {
                  setPeriodWeek(value.map((v) => v.value))
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('dashboard.storePerformance.filter.week')}
                    sx={styles.filterField}
                  />
                )}
              />
              {periodWeek.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px' }}>
                  {visibleWeeks.map((w) => (
                    <Chip
                      key={w}
                      label={`Semana ${w}`}
                      size="small"
                      onDelete={() => setPeriodWeek(periodWeek.filter((v) => v !== w))}
                      sx={{
                        backgroundColor: '#31c462',
                        color: '#fff',
                        fontSize: '12px',
                        height: '28px',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.8)',
                          '&:hover': { color: '#fff' },
                        },
                      }}
                    />
                  ))}
                  {hasMoreWeeks && (
                    <Typography
                      component="span"
                      onClick={() => setShowAllWeeks(!showAllWeeks)}
                      sx={{
                        fontSize: '13px',
                        cursor: 'pointer',
                        color: '#158bc8',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                        lineHeight: '28px',
                      }}
                    >
                      {showAllWeeks ? t('general.showLess') : t('general.showMore')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <MultiSelectSimple
              name="year"
              label={t('dashboard.storePerformance.filter.year')}
              items={getYearOptions()}
              selected={
                periodYear
                  ? {
                      id: String(periodYear),
                      label: String(periodYear),
                      value: String(periodYear),
                    }
                  : null
              }
              onChange={(e) => {
                const val = e.target.value
                setPeriodYear(val ? Number(val.value) : null)
              }}
              loading={false}
              sx={styles.filterField}
            />
          </Box>

          {isWeekError && (
            <Typography sx={{ color: 'error.main', fontSize: '12px', mt: '4px' }}>
              {t(periodValidationError)}
            </Typography>
          )}
        </Box>

        {/* Filters Section */}
        <Box>
          <Typography sx={styles.filterSectionTitle}>
            {t('dashboard.storePerformance.filter.filters')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Division */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FilterSelect
                name="division"
                label={t('dashboard.storePerformance.filter.division')}
                items={divisionsOptions}
                selected={filterDivision}
                onChange={(e: any) => setFilterDivision(e.target.value)}
              />
            </Box>

            {/* Zone */}
            {!isPartner && !hideZone && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <FilterSelect
                  name="zone"
                  label={t('dashboard.storePerformance.filter.zone')}
                  items={zonesOptions}
                  selected={filterZone}
                  onChange={(e: any) => setFilterZone(e.target.value)}
                />
              </Box>
            )}

            {/* Role */}
            {!isPartner && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <FilterSelect
                  name="role"
                  label={t('dashboard.storePerformance.filter.role')}
                  items={rolesOptions}
                  selected={filterRole}
                  onChange={(e: any) => setFilterRole(e.target.value)}
                />
              </Box>
            )}

            {/* User */}
            {!isPartner && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <FilterSelect
                  name="user"
                  label={t('dashboard.storePerformance.filter.user')}
                  items={usersOptions}
                  selected={filterUser}
                  onChange={(e: any) => setFilterUser(e.target.value)}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{ ...styles.filterButtonContainer }}>
          <Button
            variant="contained"
            onClick={onApply}
            disabled={hasPeriodErrors}
            sx={{
              ...styles.filterApplyButton,
              backgroundColor: '#31c462',
              '&:hover': {
                backgroundColor: '#28a854',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
              },
            }}
          >
            {t('dashboard.storePerformance.filter.apply')}
          </Button>
        </Box>
      </Box>
    </BaseModal>
  )
}

export { FilterModal }
